
import React, { useEffect, useMemo, useState } from 'react';
import Backend from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { ChartIcon, FaIcon } from '../../../common/Icons.jsx';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../bootstrap/Button.jsx';
import styles from '../../settings_sidebar/ag_chart/series/SeriesSettings.module.scss';
import { DragHandle, DropArea } from '../../../common/DragNDrop.jsx';
import { useGridWidgetSettings } from '../../hooks/useGridWidgetSettings.js';
import GridColumnSidebar from './GridColumnSidebar.jsx';
import TextInput from '../../../common/TextInput.jsx';
import { SIDEBAR_SETTING_TYPES } from '../../settings_sidebar/DetailSidebarSettings.jsx';
import { openDetail } from '../../redux/dashboardSlice.js';
import { useSetArrayEntityAttribute } from '../../hooks/useSetArrayEntityAttribute.js';
import DatasourceSettings from '../../settings_sidebar/datasource/DatasourceSettings.jsx';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import WidgetTitleSettings from '../../settings_sidebar/WidgetTitleSettings.jsx';
import ToggleSetting from '../../settings_sidebar/ag_chart/components/ToggleSetting.jsx';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import ToggleableSection from '../../settings_sidebar/common/ToggleableSection.jsx';
import SelectSetting from '../../settings_sidebar/common/SelectSetting.jsx';
import SettingsSection from '../../settings_sidebar/common/SettingsSection.jsx';
import SliderSetting from '../../settings_sidebar/common/SliderSetting.jsx';
import AutoHeightSettings from '../../settings_sidebar/AutoHeightSettings.jsx';

const DND_ITEM_TYPE = '#grid-column';

function extractH1(html) {
  const container = document.createElement('div');
  container.innerHTML = html;
  const h1 = container.querySelector('h1');
  return h1 ? h1.textContent : null;
}

function GridColumnNameEdit({ editingId, widgetId, setEditingId, column }) {
  const [value, setValue] = useState(column.headerName || '')
  const setAttribute = useSetArrayEntityAttribute({widgetId, entitiesSettingId: 'columns', entityId: column.id });
  const editing = editingId === column.id;

  const toggleEditing = () => {
    if (editingId === column.id) {
      setAttribute('headerName', value);
      setEditingId(null);
    } else {
      setEditingId(column.id);
    }
  }

  return (
    <div className={styles['title-edit']}>
      <h4 className='text-truncate'>
        {editing
          ? <TextInput value={value} onChange={e => setValue(e.target.value)} style={{height: 20}} />
          : (column.headerName || `${I18n.t('Column')} ${column.id}`)
        }
      </h4>
      <a onClick={toggleEditing}>
        <FaIcon type={editing ? 'check' : 'pencil'} />
      </a>
    </div>
  )
}

function GridWidgetSettings({ widgetId }) {
  const dispatch = useDispatch();
  const [openedColumn, setOpenedColumn] = useState(null);
  const { addColumn, moveColumn, removeColumn } = useGridWidgetSettings(widgetId);
  const setAttribute = useSetWidgetAttribute(widgetId);
  const columns = useSelector(s => s.dashboard.widgets[widgetId].settings.columns || []);
  const selectedColumn = useMemo(() => openedColumn && columns.find(s => s.id === openedColumn), [openedColumn, columns]);
  const [editingId, setEditingId] = useState(null);
  const columnsErrors = useSelector(state => state.dashboard.configErrors?.[widgetId]?.columns) || {}
  const selectedObject = useSelector(state => state.dashboard.widgets[widgetId].settings);
  const widgets = useSelector(state => state.dashboard.widgets);

  const selectableWidgets = useMemo(() => {
    return Object.values(widgets)
      .filter(s => s && s.widget_type === 'PageEditorWidget')
      .map(o => ({label: extractH1(o.settings.html) || o.settings.title, value: o.id}))
  }, [widgets])

  const openModal = (columnId) => {
    setOpenedColumn(columnId);
    dispatch(openDetail(`${SIDEBAR_SETTING_TYPES.grid_column}-${columnId}`));
  }

  const widgetSettingProps = {
    prefix: '',
    selectedObject,
    widgetId,
    setAttribute: (path, val) => setAttribute(`settings.${path}`, val)
  }

  return (
    <>
      <DatasourceSettings widgetId={widgetId} ChartSettingsComponent={null} />
      <hr />
      <CollapsibleSection title={I18n.t('content_settings')}>
        <SettingsSection>
          <WidgetTitleSettings widgetId={widgetId} />
          <ToggleSetting {...widgetSettingProps} label={I18n.t('use_context_menu')} attr='useContextMenu' />
          <AutoHeightSettings widgetId={widgetId} />
        </SettingsSection>
        <hr />
        <DndProvider backend={Backend} context={window}>
          <ul className={`list-group ${styles['list-group']}`}>
            {columns.map((c, idx) => (
              <DropArea
                key={idx}
                accept={DND_ITEM_TYPE}
                item={{type: DND_ITEM_TYPE, index: idx}}
                onMove={moveColumn}
                render={({ dropRef, dragRef, isDragging }) => {
                  return (
                    <div 
                      ref={dropRef} 
                      key={idx} 
                      className={`list-group-item dragHandle ${styles['list-group-item']} ${isDragging ? styles['dragging']: ''} ${columnsErrors[c.id] ? 'form-error' : ''}`}
                    >
                      <DragHandle dragRef={dragRef} sortHandleClass={styles['sort-handle']} />
                      <div className={styles['list-group-item-icon']} style={{margin: 'auto auto'}}>
                        <FaIcon type='table-columns' />
                      </div>
                      <div className={styles['list-group-item-content']}>
                        <div className={styles['list-group-item-description']}>
                          <GridColumnNameEdit
                            editingId={editingId}
                            key={c.id}
                            widgetId={widgetId}
                            column={c}
                            setEditingId={setEditingId}
                          />
                          <p className={false ? 'text-danger' : ''}>
                            {c.description || I18n.t('grid_widget_column')}
                          </p>
                        </div>
                        <div className={styles['list-group-item-actions']}>
                          <a onClick={() => openModal(c.id)}>
                            <FaIcon type='cog' style={{color: 'black'}} />
                          </a>
                          <a onClick={() => removeColumn(c.id)}>
                            <FaIcon type='trash' className='text-danger' />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                }
              />
            ))}
          </ul>
        </DndProvider>
        <div className={styles['action-button-container']}>
          <Button size='sm' onClick={addColumn}>
            <FaIcon type='plus' /> {I18n.t('add_column')}
          </Button>
        </div>
        <hr />
        <ToggleableSection {...widgetSettingProps} prefix='targetWidget' title={I18n.t('connect_to_content_block')}>
          <SelectSetting {...widgetSettingProps} options={selectableWidgets} prefix='targetWidget' attr='widgetId' label={I18n.t('content_block')}/>
        </ToggleableSection>
      </CollapsibleSection>
      <GridColumnSidebar widgetId={widgetId} selectedColumn={selectedColumn} setOpenedColumn={setOpenedColumn} />
    </>
  )
}

export default GridWidgetSettings;